import React, { memo, useEffect, useMemo, useState } from 'react';
import { Button, ButtonSize, ButtonVariant, CmhcIcon, CmhcIconSize } from '@cmhc-schl/hearth-ui';
import { Stack } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import HbtBreadcrumbComponent from 'Feature/V2/HbtBreadcrumb/components/HbtBreadcrumbComponent';
import Helmet from 'react-helmet';
import { useTranslation } from 'react-i18next';
import HbtPortfolioManagementTable from '../HbtPortfolioManagementTable';
import { ResourcesFlyout } from './component/ResourcesFlyout';
import styles from './styles.module.scss';
import SearchFlyout from 'Feature/V2/Shared_Portfolio/components/SearchFlyout';

const HbtPortfolioManagementPage = () => {
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [openFlyout, setOpenFlyout] = React.useState<boolean>(false);
  const { t } = useTranslation('HbtPortfolioManagementPage');

  const toggleDrawer = (newOpen: boolean) => {
    setOpenFlyout(newOpen);
  };

  const [openSearchFlyout, setOpenSearchFlyout] = React.useState<boolean>(false);
  const [flyoutKey, setFlyoutKey] = useState(0);
  const [prefillFromLast, setPreFillFromLast] = React.useState<boolean>(false);
  const [hasData, setHasData] = useState(true);
  const handleDataStatus = (dataExists: boolean) => {
    setHasData(dataExists);
  };

  const toggleSearchDrawer = (newOpen: boolean, prefill?: boolean) => {
    setFlyoutKey((k) => k + 1);
    setOpenSearchFlyout(newOpen);
    setPreFillFromLast(prefill || false);
  };

  const portfolioManagementFilterButtonText = useMemo(() => {
    const filterText = t('Filter');
    return selectedFilters.length === 0 ? filterText : `${filterText} (${selectedFilters.length})`;
  }, [selectedFilters.length]);

  const memoizedInventoryFilterButton = useMemo(() => {
    return (
      <Button
        name={t('Filter')}
        size={ButtonSize.SMALL}
        variant={ButtonVariant.SECONDARY}
        onClick={() => toggleDrawer(true)}
        text={{ value: portfolioManagementFilterButtonText }}
        ariaText={{ value: portfolioManagementFilterButtonText }}
        leadingIcon={() => <CmhcIcon size={CmhcIconSize.SMALL} type="icon_filter" />}
        className={!hasData ? styles.blurredFilterButton : ''}
      />
    );
  }, [portfolioManagementFilterButtonText, toggleDrawer, hasData]);

  const stickyHeader = true;
  const thContentStyles = `${stickyHeader ? styles.frozenHeader : ''}`;

  return (
    <>
      <Helmet>
        <title>{t('title')}</title>
      </Helmet>
      <HbtBreadcrumbComponent {...t('breadcrumb', { returnObjects: true })} />
      <LocalizationProvider dateAdapter={AdapterDateFns} localeText={{}}>
        <div className={styles.mainContent}>
          <div className={styles.layoutContainer}>
            <Stack direction="row" justifyContent="space-between" className={thContentStyles}>
              <div className={styles.headerContainer}>
                <div className={styles.header}>
                  <div className={styles.title} id="purposeTestReportHeading" tabIndex={0}>
                    <div>{t('PI-Title')}</div>
                    <ResourcesFlyout
                      link="/pages/master-pool"
                      linkText={t('Resources-Go-To-MPI')}
                      buttonText={t('Resources-Access-Resources')}
                    />
                  </div>
                </div>
              </div>
              <Stack
                direction="column"
                justifyContent="space-between"
                className={styles.filterTagWrapper}
              >
                <Stack direction="row" justifyContent="flex-end" spacing={2}>
                  <Button
                    type="button"
                    name="search"
                    size={ButtonSize.SMALL}
                    variant={ButtonVariant.SECONDARY}
                    leadingIcon={() => <CmhcIcon size={CmhcIconSize.SMALL} type="icon_search" />}
                    text={{
                      value: t('Portfolio-Search-Button')
                    }}
                    ariaText={{
                      value: t('Portfolio-Search-Button')
                    }}
                    onClick={() => toggleSearchDrawer(true, false)}
                    className={styles.searchButton}
                  />
                  <SearchFlyout
                    openSearchFlyout={openSearchFlyout}
                    toggleSearchDrawer={toggleSearchDrawer}
                    key={flyoutKey}
                    prefillFromLast={prefillFromLast}
                  />
                  {hasData && memoizedInventoryFilterButton}
                </Stack>
              </Stack>
            </Stack>

            <HbtPortfolioManagementTable
              toggleSearchDrawer={toggleSearchDrawer}
              openFlyout={openFlyout}
              toggleDrawer={toggleDrawer}
              updateFilters={setSelectedFilters}
              onDataStatusChange={handleDataStatus}
            />
          </div>
        </div>
      </LocalizationProvider>
    </>
  );
};

export default memo(HbtPortfolioManagementPage);
